import { useState, useEffect } from "react";
import api from "../../utils/api";
import UploadImage from "../UploadImage";

export default function EditPayout({ payout, onSuccess }) {
  const [paidAt, setPaidAt] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [note, setNote] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (payout) {
      setPaidAt(payout.paidAt?.split("T")[0] || "");
      setAmount(payout.amount || "");
      setPaymentMethod(payout.paymentMethod || "Bank Transfer");
      setNote(payout.note || "");
      setExistingImages(payout.payoutRecordUrl || []);
      setNewImages([]);
      setNewImagePreviews([]);
    }
  }, [payout]);

  const handleNewImageSelect = (file) => {
    const preview = URL.createObjectURL(file);
    setNewImages((prev) => [...prev, file]);
    setNewImagePreviews((prev) => [...prev, preview]);
  };

  const removeExistingImage = (idx) =>
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));

  const removeNewImage = (idx) => {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paidAt || !amount) {
      alert("Date and amount are required.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("paidAt", paidAt);
      formData.append("amount", parseFloat(amount));
      formData.append("paymentMethod", paymentMethod);
      formData.append("note", note);
      formData.append("existingImages", JSON.stringify(existingImages));

      newImages.forEach((file) => formData.append("payoutImageUrl", file));

      await api.patch(`/payouts/${payout._id}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Payout updated successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
    >
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Edit Payout</h2>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">
          Payment Date
        </label>
        <input
          type="date"
          value={paidAt}
          onChange={(e) => setPaidAt(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">Amount</label>
        <input
          type="text"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">
          Payment Method
        </label>
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        >
          <option value="" disabled selected>
            Select Payment
          </option>
          <option value="KBZ Bank">KBZ Bank</option>
          <option value="AYA Bank">AYA Bank</option>
          <option value="CB Bank">CB Bank</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">Note</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
          rows="3"
          placeholder="Optional note..."
        />
      </div>

      <div className="mb-6">
        <label className="block mb-2 font-medium text-gray-600">
          Payout Record Images
        </label>

        <div className="flex flex-wrap gap-2 mb-2">
          {existingImages.map((img, idx) => {
            const imgUrl = img.startsWith("http")
              ? img
              : `${process.env.REACT_APP_API_URL}${img}`;
            return (
              <div key={`existing-${idx}`} className="relative">
                <img
                  src={imgUrl}
                  alt={`Existing ${idx}`}
                  className="w-20 h-20 object-cover rounded cursor-pointer hover:scale-105 transition"
                  onClick={() => setSelectedImage(imgUrl)}
                  onError={(e) =>
                    (e.target.src = "https://via.placeholder.com/80?text=Error")
                  }
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(idx)}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  &times;
                </button>
              </div>
            );
          })}

          {newImagePreviews.map((preview, idx) => (
            <div key={`new-${idx}`} className="relative">
              <img
                src={preview}
                alt={`New ${idx}`}
                className="w-20 h-20 object-cover rounded cursor-pointer hover:scale-105 transition"
                onClick={() => setSelectedImage(preview)}
              />
              <button
                type="button"
                onClick={() => removeNewImage(idx)}
                className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        <UploadImage onFileSelect={handleNewImageSelect} />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg bg-blue-600 py-2.5 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? "Updating..." : "Update Payout"}
      </button>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="Preview"
            className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-5 right-5 text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>
      )}
    </form>
  );
}
